'use client';

import React, { useCallback, useEffect, useMemo } from 'react';

import { Section } from '../../components/common/section/Section';
import { ListingCard, ListingCardSkeleton } from '../../components/features/search/listingCard/ListingCard';
import NothingFound from '../../components/icons/NothingFound';
import { formatStartingPrice } from '../../lib/utils';

// ---- Memoized card wrapper ----
// All card visual props are passed flat (not nested in an object) so that
// React.memo's shallow comparison works correctly on primitives.
// Callbacks are bound once via useCallback with a stable activityId.

interface MemoizedListingCardItemProps {
  activityId: string;
  // Visual props (flat primitives - shallow comparison works)
  title: string;
  instructor: string;
  blurInstructor?: boolean;
  hideInstructor?: boolean;
  professionalPrefix?: string;
  location: string;
  bookings: string;
  price: string;
  pricePrefix?: string;
  imageUrl?: string;
  isHovered: boolean;
  isSelected: boolean;
  isAtHome?: boolean;
  isOnline?: boolean;
  locationKinds?: ('venue' | 'at_home' | 'online')[];
  locationKindLabels?: { venue: string; at_home: string; online: string };
  atHomeLabel?: string;
  onlineLabel?: string;
  viewActivityButtonText?: string;
  availableSlotsButtonText?: string;
  isBookingPage?: boolean;
  date?: string;
  time?: string;
  timeAgo?: string;
  isUpcoming?: boolean;
  isOngoing?: boolean;
  badge?: React.ReactNode;
  onPayNow?: () => void;
  payNowText?: string;
  ratingAverage?: number;
  ratingTotal?: number;
  priceLabel?: string;
  actionMenu?: React.ReactNode;
  // Callback props (stable references from parent)
  onActivityClick?: (id: string) => void;
  onActivityHover?: (id: string) => void;
  onActivityLeaveHover?: () => void;
  onViewActivity?: (id: string) => void;
  onViewSlots?: (id: string) => void;
  registerCardRef?: (id: string, element: HTMLDivElement | null) => void;
}

const MemoizedListingCardItem = React.memo<MemoizedListingCardItemProps>(
  ({
    activityId,
    title,
    instructor,
    blurInstructor,
    hideInstructor,
    professionalPrefix,
    location,
    bookings,
    price,
    pricePrefix,
    imageUrl,
    isHovered,
    isSelected,
    isAtHome,
    isOnline,
    locationKinds,
    locationKindLabels,
    atHomeLabel,
    onlineLabel,
    viewActivityButtonText,
    availableSlotsButtonText,
    isBookingPage,
    date,
    time,
    timeAgo,
    isUpcoming,
    isOngoing,
    badge,
    onPayNow,
    payNowText,
    ratingAverage,
    ratingTotal,
    priceLabel,
    actionMenu,
    onActivityClick,
    onActivityHover,
    onActivityLeaveHover,
    onViewActivity,
    onViewSlots,
    registerCardRef
  }) => {
    const handleClick = useCallback(() => onActivityClick?.(activityId), [activityId, onActivityClick]);
    const handleHover = useCallback(() => onActivityHover?.(activityId), [activityId, onActivityHover]);
    const handleViewActivity = useCallback(() => onViewActivity?.(activityId), [activityId, onViewActivity]);
    const handleViewSlots = useCallback(() => onViewSlots?.(activityId), [activityId, onViewSlots]);
    const handleRef = useCallback(
      (el: HTMLDivElement | null) => registerCardRef?.(activityId, el),
      [activityId, registerCardRef]
    );

    return (
      <div ref={handleRef}>
        <ListingCard
          title={title}
          instructor={instructor}
          blurInstructor={blurInstructor}
          hideInstructor={hideInstructor}
          professionalPrefix={professionalPrefix}
          location={location}
          bookings={bookings}
          price={price}
          pricePrefix={pricePrefix}
          imageUrl={imageUrl}
          isHovered={isHovered}
          isSelected={isSelected}
          isAtHome={isAtHome}
          isOnline={isOnline}
          locationKinds={locationKinds}
          locationKindLabels={locationKindLabels}
          atHomeLabel={atHomeLabel}
          onlineLabel={onlineLabel}
          viewActivityButtonText={viewActivityButtonText}
          availableSlotsButtonText={availableSlotsButtonText}
          isBookingPage={isBookingPage}
          date={date}
          time={time}
          timeAgo={timeAgo}
          isUpcoming={isUpcoming}
          isOngoing={isOngoing}
          badge={badge}
          onPayNow={onPayNow}
          payNowText={payNowText}
          ratingAverage={ratingAverage}
          ratingTotal={ratingTotal}
          priceLabel={priceLabel}
          actionMenu={actionMenu}
          onClick={handleClick}
          onHover={handleHover}
          onLeaveHover={onActivityLeaveHover}
          onViewActivity={onViewActivity ? handleViewActivity : undefined}
          onViewSlots={onViewSlots ? handleViewSlots : undefined}
        />
      </div>
    );
  }
);

MemoizedListingCardItem.displayName = 'MemoizedListingCardItem';

/**
 * ListingCardsScreen Component
 *
 * A section that displays listing cards in a grid with a status message for found activities.
 */

export interface ListingCardsScreenProps {
  /**
   * Whether to show the activity count text
   * @default true
   */
  showActivityCount?: boolean;

  /**
   * Whether to hide the instructor name on activity cards
   * @default false
   */
  hideInstructor?: boolean;

  /**
   * Array of activity objects to display
   */
  activities: {
    id: string;
    imageUrl: string;
    title: string;
    professionalName: string;
    blurProfessionalName?: boolean;
    isAtHome?: boolean;
    isOnline?: boolean;
    /** Real delivery-mode mix; 2+ kinds render as compact chips on the card. */
    locationKinds?: ('venue' | 'at_home' | 'online')[];
    bookingsSinceLastYear: number;
    /** `null`/`undefined` → no priced booking yet; price block is hidden. */
    startingPrice?: number | null;
    date?: string;
    time?: string;
    location?: string;
    status?: 'upcoming' | 'confirmed' | 'completed' | 'cancelled' | 'pending';
    currency?: string;
    timeAgo?: string;
    isUpcoming?: boolean;
    isOngoing?: boolean;
    city?: string;
    ratingAverage?: number;
    ratingTotal?: number;
    /** Optional badge element (e.g., payment status badge) */
    badge?: React.ReactNode;
    /** Handler for Pay Now action (when payment is pending) */
    onPayNow?: () => void;
    /** Text for Pay Now button */
    payNowText?: string;
    /** Optional action menu (e.g. 3-dot dropdown) rendered on the card */
    actionMenu?: React.ReactNode;
  }[];

  /**
   * Handler for viewing full activity details
   */
  onViewActivity?: (activityId: string) => void;

  /**
   * Handler for viewing available slots
   */
  onViewSlots?: (activityId: string) => void;

  /**
   * Callback to register a ref for an activity card
   */
  registerCardRef?: (id: string, element: HTMLDivElement | null) => void;

  /**
   * Total count of activities (may be different from activities.length if paginated)
   */
  totalCount: number;

  /**
   * Handler for when an activity card is clicked
   */
  onActivityClick?: (activityId: string) => void;

  /**
   * Handler for when an activity card is hovered
   */
  onActivityHover?: (activityId: string) => void;

  /**
   * Handler for when hover leaves an activity card
   */
  onActivityLeaveHover?: () => void;

  /**
   * ID of the currently hovered activity to highlight it
   */
  hoveredActivityId?: string;

  /**
   * ID of the currently selected activity
   */
  selectedActivityId?: string;

  /**
   * Whether the activities are currently loading
   */
  isLoading?: boolean;

  /**
   * Number of skeleton cards to show when loading
   * @default 3
   */
  skeletonCount?: number;

  /**
   * Whether there was an error loading the activities
   */
  isError?: boolean;

  /**
   * Translated prefix for single activity found text
   * @default ""
   */
  activitiesFoundSingularPrefix?: string;

  /**
   * Translated suffix for single activity found text
   * @default " Activity found"
   */
  activitiesFoundSingularSuffix?: string;

  /**
   * Translated prefix for multiple activities found text
   * @default ""
   */
  activitiesFoundPluralPrefix?: string;

  /**
   * Translated suffix for multiple activities found text
   * @default " Activities found"
   */
  activitiesFoundPluralSuffix?: string;

  /**
   * Translated title for no activities found message
   * @default "No activities found"
   */
  noActivitiesFoundTitle?: string;

  /**
   * Translated text for no activities found message
   * @default "No activities found. Try a different category or search term."
   */
  noActivitiesFoundText?: string;

  /**
   * Optional action rendered under the no-activities message - e.g. a button
   * that widens the search to where matches were found.
   */
  emptyStateAction?: React.ReactNode;

  /**
   * Translated text for loading message
   * @default "Loading activities..."
   */
  loadingText?: string;

  /**
   * Translated text shown when the list fails to load
   * @default "Error loading activities. Please try again later."
   */
  errorText?: string;

  /**
   * Translated text for "At Home" location
   * @default "At Home"
   */
  atHomeText?: string;

  /**
   * Translated text for "At Venue" location
   * @default "At Venue"
   */
  atVenueText?: string;

  /**
   * Translated text for an online (visio) booking
   * @default "Online"
   */
  onlineText?: string;

  /**
   * Translated prefix for bookings count text
   * @default ""
   */
  bookingsInYearPrefix?: string;

  /**
   * Translated middle text between count and year for bookings text
   * @default " Bookings in "
   */
  bookingsInYearMiddle?: string;

  /**
   * Translated suffix for bookings count text
   * @default ""
   */
  bookingsInYearSuffix?: string;

  /**
   * Translated text for price suffix ("onwards")
   * @default "onwards"
   */
  onwardsText?: string;

  /**
   * Translated label shown above the price on mobile booking cards.
   * @default "Price"
   */
  priceLabel?: string;

  /**
   * Translated prefix for professional name (e.g. "Par", "By")
   * @default "By"
   */
  professionalPrefixText?: string;

  /**
   * Translated text for "View Activity" button
   * @default "View Activity"
   */
  viewActivityButtonText?: string;

  /**
   * Translated text for "Available Slots" button
   * @default "Available Slots"
   */
  availableSlotsButtonText?: string;

  /**
   * Whether this screen is displayed on a booking page
   * If true, cards will not expand even when selected
   * @default false
   */
  isBookingPage?: boolean;

  /**
   * Handler for loading more items (infinite scroll)
   */
  onLoadMore?: () => void;

  /**
   * Whether there are more pages to load
   * @default false
   */
  hasNextPage?: boolean;

  /**
   * Whether currently loading more items
   * @default false
   */
  isLoadingMore?: boolean;

  /**
   * Text to show when loading more
   * @default "Loading more..."
   */
  loadingMoreText?: string;

  /**
   * Top activities to show in empty state (e.g., top by booking count)
   */
  topActivities?: {
    id: string;
    imageUrl: string;
    title: string;
    professionalName: string;
    blurProfessionalName?: boolean;
    isAtHome?: boolean;
    isOnline?: boolean;
    /** Real delivery-mode mix; 2+ kinds render as compact chips on the card. */
    locationKinds?: ('venue' | 'at_home' | 'online')[];
    bookingsSinceLastYear: number;
    /** `null`/`undefined` → no priced booking yet; price block is hidden. */
    startingPrice?: number | null;
    city?: string;
    ratingAverage?: number;
    ratingTotal?: number;
  }[];

  /**
   * Title for the top activities section in empty state
   * @default "Popular Activities"
   */
  topActivitiesTitle?: string;

  /**
   * Whether top activities are currently loading
   * @default false
   */
  isTopActivitiesLoading?: boolean;

  /**
   * Locale for currency formatting (e.g. 'fr-FR', 'en-US')
   * @default 'fr-FR'
   */
  locale?: string;
}

/**
 * ListingCardsScreen component for displaying a grid of listing cards with status message
 *
 * @example
 * ```tsx
 * <ListingCardsScreen
 *   activities={activities}
 *   totalCount={totalCount}
 *   onActivityClick={(id) => console.log(`Activity ${id} clicked`)}
 * />
 * ```
 */
export const ListingCardsScreen: React.FC<ListingCardsScreenProps> = ({
  activities,
  totalCount,
  onActivityClick,
  isLoading = false,
  skeletonCount = 3,
  isError = false,
  showActivityCount = true,
  hideInstructor = false,
  activitiesFoundSingularPrefix = '',
  activitiesFoundSingularSuffix = ' Activity found',
  activitiesFoundPluralPrefix = '',
  activitiesFoundPluralSuffix = ' Activities found',
  noActivitiesFoundTitle = 'No activities found',
  noActivitiesFoundText = 'No activities found. Try a different category or search term.',
  emptyStateAction,
  atHomeText = 'At Home',
  atVenueText = 'At Venue',
  onlineText = 'Online',
  bookingsInYearPrefix = '',
  bookingsInYearMiddle = ' Bookings in ',
  bookingsInYearSuffix = '',
  onwardsText = 'onwards',
  priceLabel = 'Price',
  professionalPrefixText = 'By',
  viewActivityButtonText = 'View Activity',
  availableSlotsButtonText = 'Available Slots',
  isBookingPage = false,
  onActivityHover,
  onActivityLeaveHover,
  hoveredActivityId,
  selectedActivityId,
  registerCardRef,
  onViewActivity,
  onViewSlots,
  onLoadMore,
  hasNextPage = false,
  isLoadingMore = false,
  loadingText = 'Loading activities...',
  loadingMoreText = 'Loading more...',
  errorText = 'Error loading activities. Please try again later.',
  topActivities,
  topActivitiesTitle = 'Popular Activities',
  isTopActivitiesLoading = false,
  locale = 'fr-FR'
}) => {
  const hasActivities = activities.length > 0;

  // Chip labels for the real location-kind mix (venue / at_home / online). The
  // venue chip reuses the generic "at venue" label here (per-card city stays on
  // the single-kind fallback, where there's room for it).
  const locationKindLabels = useMemo(
    () => ({ venue: atVenueText, at_home: atHomeText, online: onlineText }),
    [atVenueText, atHomeText, onlineText]
  );

  // Using boolean expressions with && for TypeScript to recognize usage
  const showLoading = isLoading && true;
  const showError = isError && true;

  // State to track the last card element for intersection observer
  const [lastCardElement, setLastCardElement] = React.useState<HTMLDivElement | null>(null);
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Enhanced registerCardRef that tracks the last card
  const enhancedRegisterCardRef = React.useCallback(
    (id: string, element: HTMLDivElement | null) => {
      // Call the original registerCardRef if provided
      if (registerCardRef) {
        registerCardRef(id, element);
      }

      // Check if this is the last card and set it as our intersection target
      const currentIndex = activities.findIndex((activity) => activity.id === id);
      const isLastCard = currentIndex === activities.length - 1;

      if (isLastCard) {
        setLastCardElement(element);
      }
    },
    [activities, registerCardRef]
  );

  // Optimized intersection observer for the last card with debouncing
  useEffect(() => {
    if (!lastCardElement || !onLoadMore || !hasNextPage || isLoadingMore) return;

    const handleIntersection = ([entry]: IntersectionObserverEntry[]) => {
      if (entry.isIntersecting) {
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Debounce the load more call to prevent rapid requests
        debounceTimeoutRef.current = setTimeout(() => {
          onLoadMore();
        }, 300);
      }
    };

    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: '200px', // Start loading when last card is 200px from viewport
      threshold: 0.1
    });

    observer.observe(lastCardElement);

    return () => {
      observer.disconnect();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [lastCardElement, onLoadMore, hasNextPage, isLoadingMore]);

  // Simple loading indicator component
  const LoadingIndicator = () => {
    if (!isLoadingMore) return null;

    return (
      <div className="flex items-center justify-center gap-3 text-gray-600 py-8">
        <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
        <span className="text-sm">{loadingMoreText}</span>
      </div>
    );
  };

  // Handle loading state with skeletons only when we still have cards to render.
  // For empty-state refetches we keep the section mounted and show an inline loader block.
  if (showLoading && hasActivities) {
    return (
      <div className="flex flex-col gap-3 w-full">
        <div className="grid grid-cols-1 gap-5 w-full">
          {Array.from({ length: skeletonCount }, (_, index) => (
            <ListingCardSkeleton key={`skeleton-${index}`} />
          ))}
        </div>
      </div>
    );
  }

  // Handle error state
  if (showError) {
    return (
      <Section className="pt-0 pb-5 md:pb-15">
        <div className="text-xs font-semibold text-red-600">{errorText}</div>
      </Section>
    );
  }

  // Show top activities when there are few results (0-2) or loading
  const shouldShowTopActivities = isTopActivitiesLoading || (topActivities && topActivities.length > 0);
  // Filter out activities that are already shown in search results to avoid duplicates
  const filteredTopActivities = topActivities?.filter(
    (topActivity) => !activities.some((activity) => activity.id === topActivity.id)
  );
  const hasFilteredTopActivities = filteredTopActivities && filteredTopActivities.length > 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Results status - shows count or "No activities" message */}
      {showActivityCount && hasActivities && (
        <div className="text-xs font-semibold text-[#1F242F]">
          {totalCount === 1
            ? `${activitiesFoundSingularPrefix}${totalCount}${activitiesFoundSingularSuffix}`
            : `${activitiesFoundPluralPrefix}${totalCount}${activitiesFoundPluralSuffix}`}
        </div>
      )}

      {/* Empty state with NothingFound icon */}
      {!hasActivities && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-full max-w-xl min-h-[240px] flex items-center justify-center mb-8 px-4">
            {showLoading ? (
              <div className="flex items-center justify-center gap-3 text-gray-600">
                <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                <span className="text-sm">{loadingText}</span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <NothingFound className="w-32 h-32 mb-6 text-neutral-400" />
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">{noActivitiesFoundTitle}</h3>
                  <p className="text-sm text-neutral-600 max-w-md">{noActivitiesFoundText}</p>
                  {emptyStateAction && <div className="mt-4 flex justify-center">{emptyStateAction}</div>}
                </div>
              </div>
            )}
          </div>

          {/* Show top activities if available or loading skeletons */}
          {shouldShowTopActivities && (
            <div className="w-full max-w-4xl">
              <h4 className="text-base font-semibold text-neutral-900 mb-4 text-left">{topActivitiesTitle}</h4>
              <div className="grid grid-cols-1 gap-5">
                {isTopActivitiesLoading
                  ? // Show skeletons when loading
                    Array.from({ length: 3 }, (_, index) => <ListingCardSkeleton key={`top-skeleton-${index}`} />)
                  : // Show actual activities when loaded
                    topActivities?.map((activity) => (
                      <MemoizedListingCardItem
                        key={activity.id}
                        activityId={activity.id}
                        title={activity.title}
                        instructor={activity.professionalName}
                        blurInstructor={activity.blurProfessionalName}
                        professionalPrefix={professionalPrefixText}
                        location={
                          activity.isOnline ? onlineText : activity.isAtHome ? atHomeText : activity.city || atVenueText
                        }
                        bookings={
                          activity.bookingsSinceLastYear && activity.bookingsSinceLastYear > 5
                            ? `${bookingsInYearPrefix}${activity.bookingsSinceLastYear}${bookingsInYearMiddle}${new Date().getFullYear()}${bookingsInYearSuffix}`
                            : ''
                        }
                        {...(formatStartingPrice(activity.startingPrice, { locale, onwardsText }) ?? {
                          price: '',
                          pricePrefix: ''
                        })}
                        imageUrl={activity.imageUrl}
                        isHovered={hoveredActivityId === activity.id}
                        isSelected={selectedActivityId === activity.id}
                        isAtHome={activity.isAtHome}
                        isOnline={activity.isOnline}
                        locationKinds={activity.locationKinds}
                        locationKindLabels={locationKindLabels}
                        atHomeLabel={atHomeText}
                        onlineLabel={onlineText}
                        viewActivityButtonText={viewActivityButtonText}
                        availableSlotsButtonText={availableSlotsButtonText}
                        isBookingPage={isBookingPage}
                        ratingAverage={activity.ratingAverage}
                        ratingTotal={activity.ratingTotal}
                        onActivityClick={onActivityClick}
                        onActivityHover={onActivityHover}
                        onActivityLeaveHover={onActivityLeaveHover}
                        onViewActivity={onViewActivity}
                        onViewSlots={onViewSlots}
                        registerCardRef={enhancedRegisterCardRef}
                      />
                    ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Listing cards grid - only render when there are activities */}
      {hasActivities && (
        <>
          <div className="grid grid-cols-1 gap-5">
            {activities.map((activity) => (
              <MemoizedListingCardItem
                key={activity.id}
                activityId={activity.id}
                title={activity.title}
                instructor={activity.professionalName}
                blurInstructor={activity.blurProfessionalName}
                hideInstructor={hideInstructor}
                professionalPrefix={professionalPrefixText}
                location={
                  activity.isOnline ? onlineText : activity.isAtHome ? atHomeText : activity.city || atVenueText
                }
                bookings={
                  activity.bookingsSinceLastYear && activity.bookingsSinceLastYear > 5
                    ? `${bookingsInYearPrefix}${activity.bookingsSinceLastYear}${bookingsInYearMiddle}${new Date().getFullYear()}${bookingsInYearSuffix}`
                    : ''
                }
                {...(formatStartingPrice(activity.startingPrice, { locale, onwardsText }) ?? {
                  price: '',
                  pricePrefix: ''
                })}
                imageUrl={activity.imageUrl}
                isHovered={hoveredActivityId === activity.id}
                isSelected={selectedActivityId === activity.id}
                isAtHome={activity.isAtHome}
                isOnline={activity.isOnline}
                locationKinds={activity.locationKinds}
                locationKindLabels={locationKindLabels}
                atHomeLabel={atHomeText}
                onlineLabel={onlineText}
                viewActivityButtonText={viewActivityButtonText}
                availableSlotsButtonText={availableSlotsButtonText}
                isBookingPage={isBookingPage}
                date={activity.date}
                time={activity.time}
                timeAgo={activity.timeAgo}
                isUpcoming={activity.isUpcoming}
                isOngoing={activity.isOngoing}
                badge={activity.badge}
                onPayNow={activity.onPayNow}
                payNowText={activity.payNowText}
                ratingAverage={activity.ratingAverage}
                ratingTotal={activity.ratingTotal}
                priceLabel={priceLabel}
                actionMenu={activity.actionMenu}
                onActivityClick={onActivityClick}
                onActivityHover={onActivityHover}
                onActivityLeaveHover={onActivityLeaveHover}
                onViewActivity={onViewActivity}
                onViewSlots={onViewSlots}
                registerCardRef={enhancedRegisterCardRef}
              />
            ))}
          </div>

          {/* Loading indicator for infinite scroll */}
          <LoadingIndicator />

          {/* Show popular activities below search results when there are few results (1-2) */}
          {(isTopActivitiesLoading || hasFilteredTopActivities) && (
            <div className="mt-8 pt-6 border-t border-neutral-200">
              <h4 className="text-base font-semibold text-neutral-900 mb-4">{topActivitiesTitle}</h4>
              <div className="grid grid-cols-1 gap-5">
                {isTopActivitiesLoading
                  ? // Show skeletons when loading
                    Array.from({ length: 3 }, (_, index) => <ListingCardSkeleton key={`top-skeleton-${index}`} />)
                  : // Show actual activities when loaded (filtered to avoid duplicates)
                    filteredTopActivities?.map((activity) => (
                      <MemoizedListingCardItem
                        key={activity.id}
                        activityId={activity.id}
                        title={activity.title}
                        instructor={activity.professionalName}
                        blurInstructor={activity.blurProfessionalName}
                        professionalPrefix={professionalPrefixText}
                        location={
                          activity.isOnline ? onlineText : activity.isAtHome ? atHomeText : activity.city || atVenueText
                        }
                        bookings={
                          activity.bookingsSinceLastYear && activity.bookingsSinceLastYear > 5
                            ? `${bookingsInYearPrefix}${activity.bookingsSinceLastYear}${bookingsInYearMiddle}${new Date().getFullYear()}${bookingsInYearSuffix}`
                            : ''
                        }
                        {...(formatStartingPrice(activity.startingPrice, { locale, onwardsText }) ?? {
                          price: '',
                          pricePrefix: ''
                        })}
                        imageUrl={activity.imageUrl}
                        isHovered={hoveredActivityId === activity.id}
                        isSelected={selectedActivityId === activity.id}
                        isAtHome={activity.isAtHome}
                        isOnline={activity.isOnline}
                        locationKinds={activity.locationKinds}
                        locationKindLabels={locationKindLabels}
                        atHomeLabel={atHomeText}
                        onlineLabel={onlineText}
                        viewActivityButtonText={viewActivityButtonText}
                        availableSlotsButtonText={availableSlotsButtonText}
                        isBookingPage={isBookingPage}
                        ratingAverage={activity.ratingAverage}
                        ratingTotal={activity.ratingTotal}
                        onActivityClick={onActivityClick}
                        onActivityHover={onActivityHover}
                        onActivityLeaveHover={onActivityLeaveHover}
                        onViewActivity={onViewActivity}
                        onViewSlots={onViewSlots}
                        registerCardRef={enhancedRegisterCardRef}
                      />
                    ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
